import {
  SHBox,
  SHCheckbox,
  SHFormControlLabel,
} from "@components/design-systems";
import { useFeeCalculatorInputField } from "@hooks/useFeeCalculatorInputField";
import { useNotification } from "@hooks/useNotification";
import { FeeCalculatorDisableConditions } from "@models/platform-analysis/entities/steps/fee";
import { AnalysisDataValue } from "@models/platform-analysis/enums/dataValue";
import { AnalysisVariable } from "@models/platform-analysis/enums/variableName";
import { isNil } from "lodash";
import { useMemo } from "react";
import { Controller } from "react-hook-form";
import { useToggle } from "react-use";
import { InvestmentSelectionPortfolioLevelTracker } from "../../trackers/investment-selection-portfolio-level-tracker";
import { FeeCalculatorVisibilityTracker } from "../../trackers/visibility-tracker";

type FeeCalculatorCheckboxControllerProps = {
  variableName: AnalysisVariable;
  disabled?: boolean;
  disableConditions?: FeeCalculatorDisableConditions;
  defaultValueWhenDisabled?: string;
};

export const FeeCalculatorCheckboxController = ({
  variableName,
  disabled,
  disableConditions,
  defaultValueWhenDisabled,
}: FeeCalculatorCheckboxControllerProps) => {
  const {
    formMethods: { control, setValue, getValues },
    fieldPath,
    fieldName,
  } = useFeeCalculatorInputField(variableName);
  const { notify } = useNotification();

  const [disableConditionMeet, toggleDisableConditionMeet] = useToggle(false);
  const [
    relatedPortfolioSetupWeightExists,
    toggleRelatedPortfolioSetupWeightExists,
  ] = useToggle(false);

  const handleOnDisableStateChange = (isDisable: boolean) => {
    toggleDisableConditionMeet(isDisable);
    if (
      !isDisable ||
      !defaultValueWhenDisabled ||
      getValues(fieldPath) === defaultValueWhenDisabled
    )
      return;
    setValue(fieldPath, defaultValueWhenDisabled);
  };

  const handleOnValueChange = (event: React.SyntheticEvent<Element, Event>) => {
    if (!("checked" in event.target)) return;
    const isChecked = event.target.checked
      ? AnalysisDataValue.True
      : AnalysisDataValue.False;

    if (
      relatedPortfolioSetupWeightExists &&
      isChecked === AnalysisDataValue.False
    ) {
      notify(
        `At least one portfolio has an allocation to ${fieldName}, set this to 0% to remove.`,
        {
          variant: "error",
          close: true,
        },
      );

      return;
    }

    return isChecked;
  };

  const shouldDisableSelection = useMemo(
    () => disabled || disableConditionMeet,
    [disabled, disableConditionMeet],
  );

  return (
    <>
      <FeeCalculatorVisibilityTracker
        renderConditions={disableConditions}
        onVisibilityChange={handleOnDisableStateChange}
        invalidIfEmpty
      />

      <InvestmentSelectionPortfolioLevelTracker
        variableName={variableName}
        onNotExistsWeightChange={toggleRelatedPortfolioSetupWeightExists}
      />

      <Controller
        name={fieldPath}
        control={control}
        render={({ field: { value, onChange, ...others } }) => (
          <SHBox>
            <SHFormControlLabel
              {...others}
              value={null}
              control={<SHCheckbox />}
              label={fieldName}
              checked={value === AnalysisDataValue.True}
              disabled={shouldDisableSelection}
              onChange={(event) => {
                const newValue = handleOnValueChange(event);
                if (isNil(newValue)) return;
                onChange(newValue);
              }}
            />
          </SHBox>
        )}
      />
    </>
  );
};
